<template>
  <label
    :class="[
      'inline-flex items-center gap-2 select-none',
      isInteractive ? 'cursor-pointer' : 'cursor-not-allowed'
    ]"
  >
    <button
      type="button"
      role="switch"
      :aria-checked="modelValue"
      :aria-busy="loading"
      :disabled="disabled || loading"
      :class="[outerClasses, sizeConfig.outer]"
      :data-testid="dataTestid || 'toggle-switch-track'"
      @click="toggle"
    >
      <span :class="[trackClasses, sizeConfig.track]">
        <span :class="[thumbClasses, sizeConfig.thumb]" />
      </span>
    </button>
    <span
      v-if="hasLabel"
      :class="['font-medium', sizeConfig.label, disabled ? 'text-gray-400' : 'text-textPrimary']"
    >
      <slot>{{ label }}</slot>
    </span>
  </label>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue'

export type SwitchSize = 'small' | 'medium'

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    label?: string
    disabled?: boolean
    loading?: boolean
    size?: SwitchSize
    dataTestid?: string
  }>(),
  {
    label: undefined,
    disabled: false,
    loading: false,
    size: 'medium',
    dataTestid: undefined
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const slots = useSlots()

const isInteractive = computed(() => !props.disabled && !props.loading)
const hasLabel = computed(() => !!props.label || !!slots.default)

function toggle() {
  if (!isInteractive.value) return
  emit('update:modelValue', !props.modelValue)
}

const SIZE_CONFIG: Record<
  SwitchSize,
  { outer: string; track: string; thumb: string; label: string }
> = {
  small: {
    outer: 'w-[38px] h-[22px] p-[3px]',
    track: 'w-full h-full p-[2px]',
    thumb: 'w-3 h-3',
    label: 'text-xs'
  },
  medium: {
    outer: 'w-[50px] h-[30px] p-[3px]',
    track: 'w-full h-full p-[3px]',
    thumb: 'w-[18px] h-[18px]',
    label: 'text-sm'
  }
}

const sizeConfig = SIZE_CONFIG[props.size]

const outerClasses =
  'flex items-center justify-center rounded-full bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'

const trackClasses = computed(() => [
  'relative inline-flex items-center rounded-full transition-colors duration-200',
  props.modelValue
    ? props.disabled
      ? 'bg-primary30/40'
      : 'bg-primary30'
    : props.disabled
      ? 'bg-lightGray30/60'
      : 'bg-lightGray30'
])

const thumbClasses = computed(() => [
  'rounded-full bg-white shadow-sm transition-transform duration-200',
  props.loading ? 'animate-pulse' : '',
  props.modelValue
    ? props.size === 'small'
      ? 'translate-x-[16px]'
      : 'translate-x-[20px]'
    : 'translate-x-0'
])
</script>
